import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import ChipPopoverContent from './ChipPopoverContent';
import {getSharedSmartChipProps, SharedSmartChipProps} from './ChipUtil';
import SmartChip from './SmartChip';
import useSmartChipQuery, {ChipItem, SmartChipType} from './useSmartChipQuery';

export interface ReactionChipProps extends SharedSmartChipProps<SmartChipType.REACTION> {}

function getReactionSourceEntryUrl(reaction: ChipItem<SmartChipType.REACTION>): string {
  const {entry} = reaction;
  if (entry == null || isInaccessible(entry)) {
    return '';
  }
  return entry.editUrl;
}

function ReactionChipAuthors({authors}: {authors: ChipItem<SmartChipType.REACTION>['authors']}): JSX.Element {
  return (
    <>
      {authors.map((author) => (
        <span key={author.handle}>
          <Avatar entity={author} size={AvatarSize.XSMALL} />
          &nbsp;{author.name}
        </span>
      ))}
    </>
  );
}

function ReactionChipPopoverContent({reaction}: {reaction: ChipItem<SmartChipType.REACTION>}): JSX.Element {
  return (
    <ChipPopoverContent
      info={{
        fields: [
          {
            label: 'Name',
            value: (
              <>
                <BenchlingIcon icon={ModelTypeConstants.REACTION} /> &nbsp; {reaction.name}
              </>
            ),
          },
          {
            label: 'Authors',
            value: reaction.authors.length > 0 ? <ReactionChipAuthors authors={reaction.authors} /> : null,
          },
          {label: 'Created', value: readableTime(reaction.createdAt)},
          {label: 'Modified', value: readableTime(reaction.modifiedAt)},
        ],
      }}
    />
  );
}

export default function ReactionChip(props: ReactionChipProps): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id, onClick} = sharedSmartChipProps;

  const {
    item: reaction,
    handleClickLink,
    isInaccessible: isReactionInaccessible,
    itemUrl,
  } = useSmartChipQuery<SmartChipType.REACTION>({
    id,
    onClick,
    getUrlFn: getReactionSourceEntryUrl,
  });

  return (
    <SmartChip
      {...sharedSmartChipProps}
      icon={ModelTypeConstants.REACTION}
      isInaccessible={isReactionInaccessible}
      isLink={(sharedSmartChipProps.isLink ?? true) && Boolean(itemUrl)}
      item={reaction}
      onClick={handleClickLink}
      popoverContentOverride={reaction != null ? <ReactionChipPopoverContent reaction={reaction} /> : null}
      url={itemUrl || null}
    />
  );
}
